import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  History,
  Loader2,
  Mail,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useCustomerNotificationLog, formatNotifiedAt } from "@/hooks/useCustomerNotificationLog";
import { useIgnoredSkus } from "@/hooks/useIgnoredSkus";
import { useKlantInlichtenPermission } from "@/hooks/useKlantInlichtenPermission";
import { useResolveOrderidFromVoNumber } from "@/hooks/useResolveOrderidFromVoNumber";
import Button3D from "@/components/orders/Button3D";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MailDraftEditor } from "./klant-inlichten/MailDraftEditor";
import { ProductSelector } from "./klant-inlichten/ProductSelector";
import {
  TEMPLATE_LABEL,
  TEMPLATE_OPTIONS,
  buildSubjectAndBody,
  describeProductLine,
  extractNameFromBilling,
  parseKleurFromCustomFields,
} from "./klant-inlichten/templates";
import type { MailDraft, OrderProductLine, TemplateKey } from "./klant-inlichten/types";
import { WizardStage } from "./klant-inlichten/WizardStage";

const TEST_RECIPIENT_DISPLAY = "j.c.casasdiaz@gmail.com en mzuiderent@decoprof.nl";

type Stage =
  | "idle"
  | "productSelector"
  | "pathChoice"
  | "singleTemplatePicker"
  | "sameTemplate"
  | "wizard"
  | "review"
  | "sending";

interface Props {
  orderid: number | string | null | undefined;
  voNumber?: string | null;
  size?: "sm" | "md";
  className?: string;
  customerNameHint?: string | null;
}

const normalizeOrderid = (value: number | string | null | undefined): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const stripHtml = (html: string): string => {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.innerText || doc.body.textContent || "").trim();
  } catch {
    return html.replace(/<[^>]+>/g, "").trim();
  }
};

const groupDraftsByTemplate = (
  productLines: OrderProductLine[],
  templateChoices: Record<string, TemplateKey | undefined>,
): MailDraft[] => {
  const grouped = new Map<TemplateKey, OrderProductLine[]>();

  for (const productLine of productLines) {
    const templateKey = templateChoices[productLine.id];
    if (!templateKey) continue;

    const existing = grouped.get(templateKey) ?? [];
    existing.push(productLine);
    grouped.set(templateKey, existing);
  }

  return Array.from(grouped.entries()).map(([template_key, lines]) => ({
    template_key,
    ...buildSubjectAndBody(template_key, lines),
    productLines: lines,
  }));
};

export const KlantInlichtenButton: React.FC<Props> = ({
  orderid: orderidProp,
  voNumber,
  size = "sm",
  className,
  customerNameHint,
}) => {
  const canSee = useKlantInlichtenPermission();
  const { user } = useAuth();
  const { ignoredSkus } = useIgnoredSkus();

  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>("idle");
  const [confirmResendOpen, setConfirmResendOpen] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  const [productLines, setProductLines] = useState<OrderProductLine[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [wizardChoices, setWizardChoices] = useState<Record<string, TemplateKey | undefined>>({});
  const [wizardIndex, setWizardIndex] = useState(0);
  const [drafts, setDrafts] = useState<MailDraft[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const normalizedOrderid = normalizeOrderid(orderidProp);
  const normalizedVoNumber = voNumber?.trim() || null;
  const resolvedFromVo = useResolveOrderidFromVoNumber(
    normalizedOrderid == null ? normalizedVoNumber : null,
  );
  const resolvedOrderid = normalizedOrderid ?? resolvedFromVo.orderid;
  const isResolvingOrderid =
    normalizedOrderid == null && Boolean(normalizedVoNumber) && resolvedFromVo.isLoading;

  const { history, isInformed } = useCustomerNotificationLog(resolvedOrderid ?? undefined);

  const buttonClass = useMemo(() => {
    const sizeClass = size === "md" ? "h-9 w-9" : "h-7 w-7";

    return cn(
      "inline-flex items-center justify-center rounded-md border transition-colors",
      sizeClass,
      "disabled:cursor-not-allowed disabled:opacity-60",
      isInformed
        ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
        : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700",
      className,
    );
  }, [className, isInformed, size]);

  const selectedProductLines = productLines.filter((line) => selectedIds.has(line.id));
  const totalSelectedProducts = selectedProductLines.length;
  const primaryProductLine = selectedProductLines[0] ?? productLines[0] ?? null;

  const naamForGreeting = useMemo(() => {
    if (!primaryProductLine) return "";

    const voornaam = primaryProductLine.voornaam_klant?.trim() ?? "";
    const achternaam = primaryProductLine.achternaam_klant?.trim() ?? "";
    const billingName = extractNameFromBilling(primaryProductLine.billing_address);
    const hintName = (customerNameHint ?? "").trim();

    return voornaam || achternaam || billingName || hintName || "";
  }, [primaryProductLine, customerNameHint]);


  const isMissingBothOrderidAndVo = resolvedOrderid == null && !normalizedVoNumber;
  const isLookupMissingOrder =
    resolvedOrderid == null && Boolean(normalizedVoNumber) && !isResolvingOrderid;
  const isButtonDisabled = isMissingBothOrderidAndVo || isLookupMissingOrder || isResolvingOrderid;

  const resetState = () => {
    setStage("idle");
    setProductLines([]);
    setSelectedIds(new Set());
    setWizardChoices({});
    setWizardIndex(0);
    setDrafts([]);
    setExpandedIds(new Set());
    setConfirmResendOpen(false);
    setLoadingLines(false);
  };

  const handleClose = () => {
    setOpen(false);
    resetState();
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectedId = (lineId: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const loadOrderLines = async (): Promise<OrderProductLine[] | null> => {
    if (resolvedOrderid == null) {
      toast.error(
        normalizedVoNumber
          ? `Order niet gevonden in orders_live_ls voor VO ${normalizedVoNumber}`
          : "Geen order gekoppeld aan deze melding",
      );
      return null;
    }

    setLoadingLines(true);

    try {
      const { data, error } = await supabase
        .from("orders_live_ls")
        .select(
          "id, orderid, ordernumber, sku, producttitle, varianttitle, brand_title, customfieldcolor, custom_fields, voornaam_klant, achternaam_klant, emailadres_klant, billing_address",
        )
        .eq("orderid", resolvedOrderid)
        .limit(100);

      if (error) throw error;

      const mappedLines: OrderProductLine[] = ((data ?? []) as Array<Record<string, unknown>>)
        .filter((row) => row.id != null)
        .map((row) => ({
          id: String(row.id),
          orderid: Number(row.orderid),
          ordernumber: row.ordernumber ? String(row.ordernumber) : null,
          sku: row.sku ? String(row.sku) : null,
          producttitle: row.producttitle ? String(row.producttitle) : null,
          varianttitle: row.varianttitle ? String(row.varianttitle) : null,
          brand_title: row.brand_title ? String(row.brand_title) : null,
          customfieldcolor: row.customfieldcolor ? String(row.customfieldcolor) : null,
          custom_fields: row.custom_fields ?? null,
          voornaam_klant: row.voornaam_klant ? String(row.voornaam_klant) : null,
          achternaam_klant: row.achternaam_klant ? String(row.achternaam_klant) : null,
          emailadres_klant: row.emailadres_klant ? String(row.emailadres_klant) : null,
          billing_address: (row.billing_address as Record<string, unknown> | null) ?? null,
          color:
            parseKleurFromCustomFields(row.custom_fields) ||
            (row.customfieldcolor ? String(row.customfieldcolor) : ""),
        }));

      const filteredLines = mappedLines.filter((line) => !line.sku || !ignoredSkus.has(line.sku));

      if (filteredLines.length === 0) {
        toast.error("Geen productregels beschikbaar na gratis-filter");
        return null;
      }

      return filteredLines;
    } catch (error: any) {
      toast.error(`Kon orderregels niet laden: ${error?.message ?? error}`);
      return null;
    } finally {
      setLoadingLines(false);
    }
  };

  const openFlow = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isButtonDisabled) return;

    setOpen(true);
    resetState();

    const lines = await loadOrderLines();
    if (!lines) return;

    setProductLines(lines);

    if (lines.length === 1) {
      setSelectedIds(new Set([lines[0].id]));
      setStage("singleTemplatePicker");
      return;
    }

    setSelectedIds(new Set());
    setStage("productSelector");
  };

  const goToNextAfterProductSelector = () => {
    if (selectedProductLines.length === 0) return;
    if (selectedProductLines.length === 1) {
      setStage("singleTemplatePicker");
      return;
    }
    setStage("pathChoice");
  };

  const buildSingleOrQuickDrafts = (templateKey: TemplateKey) => {
    const nextDrafts: MailDraft[] = [
      {
        template_key: templateKey,
        ...buildSubjectAndBody(templateKey, selectedProductLines),
        productLines: selectedProductLines,
      },
    ];

    setDrafts(nextDrafts);
    setStage("review");
  };

  const startWizard = () => {
    setWizardChoices({});
    setWizardIndex(0);
    setStage("wizard");
  };

  const handleWizardPick = (templateKey: TemplateKey) => {
    const currentProductLine = selectedProductLines[wizardIndex];
    if (!currentProductLine) return;

    setWizardChoices((previous) => {
      const next = { ...previous, [currentProductLine.id]: templateKey };

      if (wizardIndex < selectedProductLines.length - 1) {
        setWizardIndex((current) => current + 1);
      } else {
        setDrafts(groupDraftsByTemplate(selectedProductLines, next));
        setStage("review");
      }

      return next;
    });
  };

  const updateDraft = (index: number, patch: Partial<Pick<MailDraft, "subject" | "body">>) => {
    setDrafts((previous) =>
      previous.map((draft, draftIndex) =>
        draftIndex === index ? { ...draft, ...patch } : draft,
      ),
    );
  };

  const sendAllDrafts = async () => {
    if (!primaryProductLine || drafts.length === 0) return;

    for (const draft of drafts) {
      if (!draft.subject.trim() || !draft.body.trim()) {
        toast.error("Onderwerp en bericht zijn verplicht voor elke mail");
        return;
      }
    }

    setStage("sending");

    try {
      for (const draft of drafts) {
        const response = await supabase.functions.invoke("send-customer-notification", {
          body: {
            orderid: primaryProductLine.orderid,
            template_key: draft.template_key,
            subject: draft.subject,
            message_text: draft.body,
            voornaam: naamForGreeting.length > 0 ? naamForGreeting : null,
            sent_by: user?.username ?? user?.id ?? null,
          },
        });

        if (response.error) throw response.error;
        if (!response.data?.success) {
          throw new Error(response.data?.error ?? "Versturen mislukt");
        }
      }


      toast.success(
        drafts.length === 1
          ? `Verstuurd naar TESTADRES (${TEST_RECIPIENT_DISPLAY})`
          : `${drafts.length} mails verstuurd naar TESTADRES (${TEST_RECIPIENT_DISPLAY})`,
      );
      setOpen(false);
      resetState();
    } catch (error: any) {
      toast.error(`Versturen mislukt: ${error?.message ?? error}`);
      setStage("review");
    }
  };

  const handleSend = () => {
    if (isInformed) {
      setConfirmResendOpen(true);
      return;
    }

    void sendAllDrafts();
  };

  const handleConfirmResend = async () => {
    setConfirmResendOpen(false);
    await sendAllDrafts();
  };

  const reviewBackStage = (): Stage => {
    if (totalSelectedProducts <= 1) return "singleTemplatePicker";
    return Object.keys(wizardChoices).length > 0 ? "wizard" : "sameTemplate";
  };

  const renderStage = () => {
    if (loadingLines) {
      return (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Orderdata laden...
        </div>
      );
    }

    if (productLines.length === 0) {
      return <div className="py-6 text-sm text-muted-foreground">Geen data beschikbaar.</div>;
    }

    if (stage === "productSelector") {
      return (
        <ProductSelector
          productLines={productLines}
          selectedIds={selectedIds}
          onToggle={toggleSelectedId}
          onContinue={goToNextAfterProductSelector}
        />
      );
    }

    if (stage === "pathChoice") {
      return (
        <div className="space-y-3">
          <div className="text-sm font-medium text-foreground">Kies je werkwijze</div>
          <div className="grid gap-2">
            <Button3D tone="primary" className="w-full justify-start" onClick={() => setStage("sameTemplate")}>
              Zelfde sjabloon voor alle
            </Button3D>
            <Button3D tone="neutral" className="w-full justify-start" onClick={startWizard}>
              Stap voor stap per product
            </Button3D>
          </div>
        </div>
      );
    }

    if (stage === "singleTemplatePicker" || stage === "sameTemplate") {
      return (
        <div className="space-y-3">
          <div className="text-sm font-medium text-foreground">
            {stage === "singleTemplatePicker"
              ? "Kies een sjabloon voor dit product"
              : "Kies een sjabloon voor alle geselecteerde producten"}
          </div>
          <div className="grid gap-2">
            {TEMPLATE_OPTIONS.map((template) => (
              <Button3D
                key={template.key}
                tone="neutral"
                className="w-full justify-start"
                onClick={() => buildSingleOrQuickDrafts(template.key)}
              >
                {template.label}
              </Button3D>
            ))}
          </div>
        </div>
      );
    }

    if (stage === "wizard") {
      return (
        <WizardStage
          productLines={selectedProductLines}
          currentIndex={wizardIndex}
          choices={wizardChoices}
          onPickTemplate={handleWizardPick}
          onBack={() => setWizardIndex((current) => Math.max(0, current - 1))}
        />
      );
    }

    if (stage === "review" || stage === "sending") {
      return (
        <MailDraftEditor
          drafts={drafts}
          isSending={stage === "sending"}
          onDraftChange={updateDraft}
          onBack={() => setStage(reviewBackStage())}
          onSend={handleSend}
        />
      );
    }

    return null;
  };

  if (!canSee) return null;

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <button
                type="button"
                aria-label="Klant inlichten"
                className={buttonClass}
                onClick={openFlow}
                disabled={isButtonDisabled}
              >
                {isResolvingOrderid ? (
                  <Loader2 className={cn(size === "md" ? "h-4 w-4" : "h-3.5 w-3.5", "animate-spin")} />
                ) : (
                  <Mail className={size === "md" ? "h-4 w-4" : "h-3.5 w-3.5"} />
                )}
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {isMissingBothOrderidAndVo
              ? "Geen gekoppelde order op deze melding"
              : isResolvingOrderid
                ? "Order koppelen op basis van VO nummer..."
                : isLookupMissingOrder
                  ? `Order niet gevonden in orders_live_ls voor VO ${normalizedVoNumber}`
                  : isInformed
                    ? "Klant ingelicht, klik voor historiek"
                    : "Klant inlichten"}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            handleClose();
            return;
          }
          setOpen(nextOpen);
        }}
      >
        <DialogContent
          className="flex max-h-[90vh] max-w-3xl flex-col"
          onClick={(event) => event.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Klant inlichten bij nalevering
              <Badge variant="outline" className="ml-2 border-amber-300 bg-amber-50 text-amber-800">
                TESTMODUS
              </Badge>
            </DialogTitle>
            <DialogDescription>
              Mail wordt uitsluitend verzonden naar het testadres{" "}
              <span className="font-mono text-amber-700">{TEST_RECIPIENT_DISPLAY}</span> en niet naar de klant.
            </DialogDescription>
          </DialogHeader>

          {stage === "review" || stage === "sending" ? (
            <div className="flex min-h-0 flex-1 flex-col">
              {primaryProductLine && (
                <div className="mb-3 rounded-md border bg-slate-50 p-3 text-xs text-slate-700">
                  <div>
                    <span className="text-slate-500">Order:</span>{" "}
                    {primaryProductLine.ordernumber ?? primaryProductLine.orderid}
                  </div>
                  <div className="mt-1">
                    <span className="text-slate-500">Geselecteerd:</span>{" "}
                    {totalSelectedProducts === 0 ? "0 producten" : `${totalSelectedProducts} producten`}
                  </div>
                </div>
              )}
              <p className="mb-2 text-xs text-muted-foreground">
                Aanhef: Beste {naamForGreeting || "klant"},
              </p>
              <MailDraftEditor
                drafts={drafts}
                isSending={stage === "sending"}
                onDraftChange={updateDraft}
                onBack={() => setStage(reviewBackStage())}
                onSend={handleSend}
              />
            </div>
          ) : (
            <>
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
                {isInformed && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                    <div className="font-semibold">Let op: deze klant is recent al ingelicht over deze order.</div>
                    <div className="mt-0.5">Bekijk eerst de historiek hieronder voordat je opnieuw verstuurt.</div>
                  </div>
                )}

                {primaryProductLine && (
                  <div className="rounded-md border bg-slate-50 p-3 text-xs text-slate-700">
                    <div>
                      <span className="text-slate-500">Order:</span>{" "}
                      {primaryProductLine.ordernumber ?? primaryProductLine.orderid}
                    </div>
                    <div className="mt-1">
                      <span className="text-slate-500">Geselecteerd:</span>{" "}
                      {totalSelectedProducts === 0 ? "0 producten" : `${totalSelectedProducts} producten`}
                    </div>
                  </div>
                )}

                {(stage === "singleTemplatePicker" || stage === "sameTemplate" || stage === "wizard") && (
                  <p className="text-xs text-muted-foreground">
                    Aanhef: Beste {naamForGreeting || "klant"},
                  </p>
                )}

                {renderStage()}

                {isInformed && (
                  <div>
                    <div className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-slate-700">
                      <History className="h-3.5 w-3.5" /> Eerder verstuurd
                    </div>
                    <ul className="divide-y rounded-md border bg-background text-xs">
                      {history.map((entry) => {
                        const isExpanded = expandedIds.has(entry.id);
                        const plainBody = stripHtml(entry.body_html ?? "");
                        return (
                          <li key={entry.id}>
                            <button
                              type="button"
                              onClick={() => toggleExpanded(entry.id)}
                              className="flex w-full items-start gap-2 p-2 text-left hover:bg-muted/40"
                            >
                              {isExpanded ? (
                                <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className="font-medium text-foreground">{formatNotifiedAt(entry.sent_at)}</span>
                                  <Badge variant="secondary" className="text-[10px]">
                                    {TEMPLATE_LABEL[entry.template_key as TemplateKey] ?? entry.template_key}
                                  </Badge>
                                  <Badge
                                    variant="outline"
                                    className="flex items-center gap-1 border-slate-300 bg-slate-50 text-[10px] text-slate-700"
                                  >
                                    <User className="h-2.5 w-2.5" />
                                    {entry.sent_by ?? "onbekend"}
                                  </Badge>
                                </div>
                                <div className="mt-0.5 text-muted-foreground">naar {entry.recipient}</div>
                                <div className="truncate text-slate-600">{entry.subject}</div>
                              </div>
                            </button>

                            {isExpanded && (
                              <div className="space-y-2 border-t bg-slate-50 p-2">
                                <div>
                                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                    Onderwerp
                                  </div>
                                  <div className="text-slate-800">{entry.subject}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                    Verzonden bericht
                                  </div>
                                  {plainBody ? (
                                    <pre className="mt-1 max-h-[300px] overflow-y-auto whitespace-pre-wrap break-words rounded-md border bg-slate-50 p-2 font-sans text-xs text-slate-800">
                                      {plainBody}
                                    </pre>
                                  ) : (
                                    <div className="text-muted-foreground">Geen inhoud opgeslagen.</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {stage === "wizard" && totalSelectedProducts > 0 && (
                  <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
                    Huidig product: {describeProductLine(selectedProductLines[wizardIndex])}
                  </div>
                )}
              </div>

              <div className="mt-4 flex shrink-0 flex-wrap justify-end gap-2 border-t pt-3">
                {stage === "pathChoice" && (
                  <Button3D tone="neutral" onClick={() => setStage("productSelector")}>
                    Terug naar producten
                  </Button3D>
                )}
                {(stage === "singleTemplatePicker" || stage === "sameTemplate") && (
                  <Button3D
                    tone="neutral"
                    onClick={() => setStage(totalSelectedProducts <= 1 ? "productSelector" : "pathChoice")}
                  >
                    Terug
                  </Button3D>
                )}
                {stage === "wizard" && (
                  <Button3D tone="neutral" onClick={() => setStage("pathChoice")}>
                    Terug naar keuze
                  </Button3D>
                )}
                <Button3D tone="neutral" onClick={handleClose}>
                  Sluiten
                </Button3D>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmResendOpen} onOpenChange={setConfirmResendOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Klant is recent al ingelicht</AlertDialogTitle>
            <AlertDialogDescription>
              Er staat al minstens een verzonden bericht voor deze order in de historie ({history.length} stuks). Weet je zeker dat je opnieuw wilt versturen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button3D tone="neutral">Annuleren</Button3D>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button3D tone="primary" onClick={handleConfirmResend}>
                Toch opnieuw versturen
              </Button3D>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default KlantInlichtenButton;
